'use client';

import {
  useState
} from 'react';

import type {
  PracticeReason,
  PracticeReasonStepView,
  PracticeReasonsResult
} from '@/app/lib/practice/types';

// Euclidean geometry, "calculate, with reasons" (practice-solving-ux-spec.md
// section 5; Governor, 8 and 13 October 2026). Shown once the learner's value
// is right: each step's statement, and a reason to choose for it, from three
// likely ones (chips) or the full CAPS list. One mark per right reason, marked
// by the server once; after that every step shows the memo's reason.
//
// The statements give the value away, so the server only sends them after
// the value is right. Nothing here knows which reason is right until the
// server has marked them.

const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(1,158,250)]';

const CHIP = `min-h-[44px] text-left px-3.5 py-2 rounded-2xl border text-[15px] leading-snug transition-colors motion-reduce:transition-none disabled:cursor-default ${ FOCUS }`;

const MAIN = `min-h-[44px] px-5 rounded-full text-sm font-semibold bg-[rgb(1,158,250)] text-white hover:bg-[rgb(0,140,225)] transition-colors motion-reduce:transition-none disabled:opacity-60 ${ FOCUS }`;

/** Server-rendered MathML from our own question bank. */
function MathHtml({
  html, className = ''
}: { html: string
  className?: string }) {

  return (
    <div
      className={`practice-maths ${ className }`}
      dangerouslySetInnerHTML={{
        __html: html
      }}
    />
  );
}

export interface ReasonsPanelProps {
  questionNumber: number
  steps: PracticeReasonStepView[]
  /** The full CAPS list, for "Another reason". */
  reasonList: PracticeReason[] | null
  /** Set once marked: each step's verdict, and the memo's reasons on `steps`. */
  result: { detail: boolean[]
    picked?: string[]
    earned?: number
    total?: number } | null
  /** True once the memo is showing (marked, or the worked solution was opened first). */
  memo: boolean
  busy: boolean
  onSubmit: (picks: Record<string, string>) => void
}

export function ReasonsPanel({
  questionNumber, steps, reasonList, result, memo, busy, onSubmit
}: ReasonsPanelProps) {

  const [ picks, setPicks ] = useState<Record<string, string>>({});
  const [ missing, setMissing ] = useState(false);
  const textOf = new Map((reasonList ?? []).map((r) => [ r.id, r.text ]));
  const locked = memo || result !== null || busy;
  const total = steps.reduce((sum, s) => sum + s.reasonMarks, 0);

  const choose = (step: number, id: string) => {

    if (locked) {

      return;
    }

    setMissing(false);
    setPicks((p) => ({
      ...p,
      [String(step)]: id
    }));
  };

  const submit = () => {

    if (steps.some((s) => !picks[String(s.step)])) {

      setMissing(true);
      return;
    }

    onSubmit(picks);
  };

  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-4 space-y-4" aria-label={`Reasons for question ${ questionNumber }`}>
      <div>
        <p className="text-sm font-semibold text-[#111111] dark:text-white">Now give your reasons</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {memo && !result
            ? 'These are the reasons the memo gives.'
            : `One mark for each right reason: ${ total } ${ total === 1 ? 'mark' : 'marks' }. Choose a reason for every step, then tap Mark my reasons.`}
        </p>
      </div>

      <ol className="space-y-5">
        {steps.map((s, i) => {

          const key = String(s.step);
          const picked = result?.picked?.[i] ?? picks[key];
          const verdict = result ? result.detail[i] : undefined;
          const chipIds = new Set((s.chips ?? []).map((c) => c.id));
          const others = (reasonList ?? []).filter((r) => !chipIds.has(r.id));

          return (
            <li key={s.step} className="space-y-2">
              <span className="inline-block text-xs font-semibold text-[rgb(0,113,190)] dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 rounded-full px-2.5 py-0.5">
                {`Step ${ s.step }`}
              </span>
              <MathHtml html={s.statementHtml} className="overflow-x-auto overflow-y-hidden whitespace-nowrap py-1 text-lg text-[#111111] dark:text-gray-100" />

              {!memo && !result && (
                <>
                  {s.chips && (
                    <div role="radiogroup" aria-label={`Reason for step ${ s.step }`} className="grid grid-cols-1 gap-2">
                      {s.chips.map((chip) => {

                        const on = picked === chip.id;

                        return (
                          <button
                            key={chip.id}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            disabled={locked}
                            onClick={() => choose(s.step, chip.id)}
                            className={`${ CHIP } ${ on
                              ? 'border-[rgb(1,158,250)] bg-sky-50 dark:bg-sky-950/30 text-[#111111] dark:text-white'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:border-[rgba(1,158,250,0.5)]' }`}
                          >
                            {chip.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {others.length > 0 && (
                    <label className="block text-xs text-gray-500 dark:text-gray-400">
                      {s.chips ? 'Another reason' : 'Reason'}
                      <select
                        value={picked && !chipIds.has(picked) ? picked : ''}
                        disabled={locked}
                        onChange={(e) => choose(s.step, e.target.value)}
                        className={`mt-1 block w-full min-h-[44px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-[15px] text-[#111111] dark:text-gray-100 ${ FOCUS }`}
                      >
                        <option value="">Choose from the full list</option>
                        {others.map((r) => (
                          <option key={r.id} value={r.id}>{r.text}</option>
                        ))}
                      </select>
                    </label>
                  )}
                </>
              )}

              {(memo || result) && (
                <p className="text-[15px] leading-snug">
                  {verdict !== undefined && (
                    <span className={verdict ? 'font-semibold text-emerald-700 dark:text-emerald-400' : 'font-semibold text-red-600 dark:text-red-400'}>
                      {verdict ? 'Right: ' : `Not quite${ picked ? ` (you chose ${ textOf.get(picked) ?? picked })` : '' }. The memo says: `}
                    </span>
                  )}
                  {verdict === undefined && <span className="font-semibold text-gray-700 dark:text-gray-300">Reason: </span>}
                  <span className="text-gray-700 dark:text-gray-200">{s.memoReasonText ?? ''}</span>
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {!memo && !result && (
        <div className="space-y-2">
          <button type="button" disabled={busy} onClick={submit} className={MAIN}>
            {busy ? 'Marking…' : 'Mark my reasons'}
          </button>
          {missing && (
            <p className="text-sm text-gray-500" role="status">Choose a reason for every step first.</p>
          )}
        </div>
      )}

      {result && result.earned !== undefined && result.total !== undefined && (
        <p className="text-[15px] font-semibold text-[#111111] dark:text-white" role="status">
          {`Reasons: ${ result.earned } of ${ result.total } ${ result.total === 1 ? 'mark' : 'marks' }.`}
        </p>
      )}
    </div>
  );
}

/** The marked result as the panel shows it. */
export function reasonsResultView(result: PracticeReasonsResult): { steps: PracticeReasonStepView[]
  detail: boolean[]
  picked: string[]
  earned: number
  total: number } {

  return {
    steps: result.steps,
    detail: result.steps.map((s) => s.correct),
    picked: result.steps.map((s) => s.picked),
    earned: result.reasonMarksEarned,
    total: result.reasonMarksTotal
  };
}
